import React, { useEffect, useRef, useState, Suspense, lazy } from 'react';
import { useAppDispatch } from './store/hooks';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import { Box, CircularProgress } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider, useSelector } from 'react-redux';
import { store, RootState } from './store/store';
import { getCurrentUser } from './store/slices/authSlice';
import { installCrossTabLogout } from './store/crossTabAuth';
import { purgeLegacyLocalDesigns } from './utils/designDraft';
import { syncGuestCartAsync, fetchCart } from './store/slices/cartSlice';
import { useWishlistSync } from './hooks/useWishlist';
import { initGA } from './config/analytics';
import { usePageTracking } from './hooks/useAnalytics';
import { configureDOMPurify } from './utils/sanitize';
import { initSentry, setSentryUser } from './config/sentry';

// Components - eagerly loaded
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import NewsletterPopup from './components/NewsletterPopup';
import ErrorBoundary from './components/ErrorBoundary';
import ShopGate from './components/shop/ShopGate';
import { NotificationProvider } from './contexts/NotificationContext';

// Pages - lazy loaded with code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const DesignEditorPage = lazy(() => import('./pages/DesignEditorPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const AdminShell = lazy(() => import('./components/admin/AdminShell'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const QuotesPage = lazy(() => import('./pages/QuotesPage'));
const QuoteDetailPage = lazy(() => import('./pages/QuoteDetailPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const ShippingInfoPage = lazy(() => import('./pages/ShippingInfoPage'));
const ReturnPolicyPage = lazy(() => import('./pages/ReturnPolicyPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const PaymentMethodsPage = lazy(() => import('./pages/PaymentMethodsPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const NewsletterConfirmPage = lazy(() => import('./pages/NewsletterConfirmPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
// 7. lépés — új publikus oldalak
const TrackOrderPage = lazy(() => import('./pages/TrackOrderPage'));
const ProofApprovalPage = lazy(() => import('./pages/ProofApprovalPage'));
const QuickOrderPage = lazy(() => import('./pages/QuickOrderPage'));
const CorporateQuotePage = lazy(() => import('./pages/CorporateQuotePage'));
const JoinGroupPage = lazy(() => import('./pages/JoinGroupPage'));
const GroupPage = lazy(() => import('./pages/GroupPage'));
const MuhelyShell = lazy(() => import('./muhely/MuhelyShell'));

const PageSpinner: React.FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress size={60} />
  </Box>
);

// Webshop branch: header/footer Layout, newsletter popup, public + customer routes
const ShopBranch: React.FC = () => {
  const [showNewsletterPopup, setShowNewsletterPopup] = useState(false);

  useEffect(() => {
    // Show newsletter popup after 10 seconds on first visit
    const hasShownPopup = localStorage.getItem('newsletter_popup_shown');

    if (!hasShownPopup) {
      const timer = setTimeout(() => {
        setShowNewsletterPopup(true);
      }, 10000); // 10 seconds delay

      return () => clearTimeout(timer);
    }
  }, []);

  // Az útvonalak egyszer definiálva: nyitott boltban (és staff-előnézetben) a bolti
  // keretben, zárt boltban a mindig elérhető oldalakhoz a ShopGate minimális keretében.
  const routes = (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/termekek" element={<ProductsPage />} />
        <Route path="/termek/:slug" element={<ProductDetailPage />} />
        <Route path="/kereses" element={<SearchPage />} />
        <Route path="/rolunk" element={<AboutPage />} />

        {/* Auth routes - redirect to home if already logged in */}
        <Route
          path="/bejelentkezes"
          element={
            <ProtectedRoute requireAuth={false}>
              <LoginPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/elfelejtett-jelszo"
          element={
            <ProtectedRoute requireAuth={false}>
              <ForgotPasswordPage />
            </ProtectedRoute>
          }
        />
        <Route path="/jelszo-visszaallitas/:uid/:token" element={<ResetPasswordPage />} />
        <Route path="/jelszo-visszaallitas" element={<ResetPasswordPage />} />
        <Route
          path="/regisztracio"
          element={
            <ProtectedRoute requireAuth={false}>
              <RegisterPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/profil"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kosar"
          element={<CartPage />}
        />
        <Route
          path="/kivansaglista"
          element={<WishlistPage />}
        />
        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/megrendeles-sikeres"
          element={<OrderSuccessPage />}
        />
        {/* Design Editor - standalone and product-specific */}
        <Route
          path="/design-editor"
          element={
            <ProtectedRoute>
              <DesignEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/design-editor/:productId"
          element={
            <ProtectedRoute>
              <DesignEditorPage />
            </ProtectedRoute>
          }
        />
        <Route path="/keszlet" element={<Navigate to="/muhely/keszlet" replace />} />

        {/* Orders pages */}
        <Route
          path="/rendelesek"
          element={
            <ProtectedRoute>
              <OrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rendelesek/:id"
          element={
            <ProtectedRoute>
              <OrderDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fizetes/:orderId"
          element={
            <ProtectedRoute>
              <PaymentPage />
            </ProtectedRoute>
          }
        />

        {/* Quote pages */}
        <Route
          path="/arajanlataim"
          element={
            <ProtectedRoute>
              <QuotesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/arajanlataim/:id"
          element={
            <ProtectedRoute>
              <QuoteDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Contact page */}
        <Route path="/kapcsolat" element={<ContactPage />} />
        {/* 7. lépés — új publikus oldalak (nincs ProtectedRoute) */}
        <Route path="/rendeles-kovetese" element={<TrackOrderPage />} />
        <Route path="/latvanyterv/:token" element={<ProofApprovalPage />} />
        <Route path="/latvanyterv" element={<ProofApprovalPage />} />
        <Route path="/gyors-rendeles" element={<QuickOrderPage />} />
        <Route path="/ceges-ajanlat" element={<CorporateQuotePage />} />
        {/* Zárt csoport meghívó link — vendégnek belépés / regisztráció után csatlakoztat */}
        <Route path="/csatlakozas/:kod" element={<JoinGroupPage />} />
        {/* Zárt csoport oldala — csak tagnak (és munkatársnak); másnak 404-es üzenet */}
        <Route
          path="/csoport/:slug"
          element={
            <ProtectedRoute>
              <GroupPage />
            </ProtectedRoute>
          }
        />

        {/* Legal pages */}
        <Route path="/aszf" element={<TermsAndConditionsPage />} />
        <Route path="/adatvedelem" element={<PrivacyPolicyPage />} />
        <Route path="/szallitas" element={<ShippingInfoPage />} />
        <Route path="/visszakuldesi-szabalyzat" element={<ReturnPolicyPage />} />

        {/* Customer support pages */}
        <Route path="/gyik" element={<FaqPage />} />
        <Route path="/fizetesi-modok" element={<PaymentMethodsPage />} />

        {/* Redirects — footer aliases */}
        <Route path="/szallitasi-modok" element={<Navigate to="/szallitas" replace />} />
        <Route path="/ugyfelszolgalat" element={<Navigate to="/kapcsolat" replace />} />
        <Route path="/arajanlatkeres" element={<Navigate to="/kapcsolat" replace />} />

        {/* GDPR pages */}
        <Route path="/leiratkozas" element={<UnsubscribePage />} />
        <Route path="/leiratkozas/:token" element={<UnsubscribePage />} />
        <Route path="/hirlevel/megerosites/:token" element={<NewsletterConfirmPage />} />
        <Route path="/hirlevel/megerosites" element={<NewsletterConfirmPage />} />

        {/* Catch all route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );

  return (
    <ShopGate bareRoutes={routes}>
      <NewsletterPopup
        open={showNewsletterPopup}
        onClose={() => setShowNewsletterPopup(false)}
      />
      <Layout>{routes}</Layout>
    </ShopGate>
  );
};

// App content component to handle authentication check
const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token, user } = useSelector((state: RootState) => state.auth);
  const { items: cartItems, isGuest } = useSelector((state: RootState) => state.cart);

  // Initialize Google Analytics, DOMPurify, and Sentry
  useEffect(() => {
    initGA();
    configureDOMPurify();
    initSentry();
  }, []);

  // Másik lapon kijelentkezés → itt is (pl. a tervező piszkozat-mentése leáll).
  useEffect(() => installCrossTabLogout(store), []);

  // Régi tervező „Mentés"-ének maradékai (base64 fotók a localStorage-ban) — adatvédelem.
  useEffect(() => {
    purgeLegacyLocalDesigns();
  }, []);

  // Track page views
  usePageTracking();

  // Kívánságlista: vendég-tételek feltöltése + szerveres lista betöltése
  // egyszer, itt (nem minden WishlistButton-példányban).
  useWishlistSync();

  // A vendég-kosár a bejelentkezés pillanatában számít — ref-ből olvassuk, hogy az
  // effect csak token-váltáskor fusson (kosár-változásra ne szinkronizáljon újra).
  const guestCartRef = useRef({ isGuest, cartItems });
  guestCartRef.current = { isGuest, cartItems };

  useEffect(() => {
    // Check if user is logged in and fetch user data
    if (token) {
      dispatch(getCurrentUser());
      const guest = guestCartRef.current;
      if (guest.isGuest && guest.cartItems.length > 0) {
        // Merge guest cart into server cart, then load the merged cart
        dispatch(syncGuestCartAsync(guest.cartItems));
      } else {
        dispatch(fetchCart());
      }
    }
  }, [dispatch, token]);

  // Sync user info with Sentry
  useEffect(() => {
    setSentryUser(user);
  }, [user]);

  return (
    <Routes>
      {/* Belső műhely app — saját shell, webshop header/footer nélkül */}
      <Route
        path="/muhely/*"
        element={
          <ProtectedRoute requireAdmin>
            <Suspense fallback={<PageSpinner />}>
              <MuhelyShell />
            </Suspense>
          </ProtectedRoute>
        }
      />
      {/* Operátori admin — saját shell, webshop header/footer nélkül (A60) */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requireAdmin>
            <Suspense fallback={<PageSpinner />}>
              <AdminShell />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route path="/*" element={<ShopBranch />} />
    </Routes>
  );
};

// Adat-router (createBrowserRouter): a meglévő <Routes> fa egyetlen „*” útvonal alatt
// fut — így működik a useBlocker (pl. mentetlen főoldal-szerkesztés elhagyásakor).
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <ErrorBoundary>
        <AppContent />
      </ErrorBoundary>
    ),
  },
]);

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <NotificationProvider>
          <RouterProvider router={router} />
        </NotificationProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
